import { useEffect, useState } from 'react';
import { getSiteImages } from './site-api';

// Slugs the admin Site Images page manages. Keep in sync with
// cherry-picked-admin/src/app/(authed)/site-images/page.tsx.
export const HERO_SLUGS = ['hero_1', 'hero_2', 'hero_3'] as const;

/**
 * Fetches the admin-managed landing-page images once and returns helpers
 * to resolve each slot, always falling back to the page's bundled asset
 * so nothing renders blank if a slot is unset or the request fails.
 *
 *   const { resolve, heroList } = useSiteImages();
 *   <img src={resolve('about_us', aboutFallback)} />
 *   const heroes = heroList([heroFallback]);  // for the slideshow
 */
export function useSiteImages() {
  const [map, setMap] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    getSiteImages()
      .then((res) => {
        if (cancelled) return;
        const next: Record<string, string> = {};
        for (const row of res.data ?? []) {
          if (row.image_url_preview_url) {
            next[row.slug] = row.image_url_preview_url;
          }
        }
        setMap(next);
      })
      .catch(() => {
        // Leave map empty — every consumer falls back to its bundled asset.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Admin image for `slug` if set, else the bundled fallback.
  const resolve = (slug: string, fallback: string): string =>
    map[slug] ?? fallback;

  // The set hero slides (in order), or the supplied fallback list when
  // the admin hasn't uploaded any.
  const heroList = (fallback: string[]): string[] => {
    const list = HERO_SLUGS.map((s) => map[s]).filter(
      (v): v is string => Boolean(v),
    );
    return list.length ? list : fallback;
  };

  return { resolve, heroList, map };
}
